import { useEffect, useMemo, useState } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { deepMergeObjects } from '../../utils/deep-merge-objects';
import { formatForClient } from '../../utils/format-line-item';

type UseRequestLineItemsParams = {
  requestId: number;
};

type LineItemRow = TrpcOutputs['getRequestLineItems'][number];

export const useRequestLineItems = ({ requestId }: UseRequestLineItemsParams) => {
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  const enabled = !!requestId;
  const lineItemIds = useMemo(() => lineItems.map((lineItem) => lineItem.id), [lineItems]);

  const {
    data: initialData,
    isLoading,
    error,
  } = trpc.getRequestLineItems.useQuery({ requestId }, { refetchOnWindowFocus: false, enabled });

  useEffect(() => {
    if (initialData) {
      setLineItems(initialData.map(formatForClient));
    }
  }, [initialData]);

  trpc.onRequestLineItemCreated.useSubscription(
    { requestId, includeParentsOnly: true },
    {
      enabled,
      onData: (newData: LineItemRow) => {
        const nextLineItem = formatForClient(newData);
        setLineItems((prevData) => {
          if (prevData.some((lineItem) => lineItem.id === nextLineItem.id)) return prevData;
          return [...prevData, nextLineItem];
        });
      },
      onError: (err: unknown) => console.error('onLineItemCreated error:', err),
    },
  );

  trpc.onRequestLineItemUpdated.useSubscription(
    { requestId, lineItemIds },
    {
      enabled,
      onData: (newData: LineItemRow) => {
        const nextLineItem = formatForClient(newData);
        setLineItems((prevData) => {
          const existingLineItem = prevData.find((lineItem) => lineItem.id === nextLineItem.id);
          if (!existingLineItem) return [...prevData, nextLineItem];

          return prevData.map((lineItem) =>
            lineItem.id !== nextLineItem.id ? lineItem : deepMergeObjects(lineItem, nextLineItem),
          );
        });
      },
      onError: (err: unknown) => console.error('onLineItemUpdated error:', err),
    },
  );

  trpc.onLineItemDeleted.useSubscription(
    { requestId, lineItemIds },
    {
      enabled,
      onData: (deletedId: number) => {
        setLineItems((prevData) => prevData.filter((lineItem) => lineItem.id !== deletedId));
      },
      onError: (err: unknown) => console.error('onLineItemDeleted error:', err),
    },
  );

  return { lineItems, isLoading, error };
};
